import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Recipe } from '@/types/recipe';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { MealPlan } from '@/types/mealPlan';
import { CalendarDays, ChefHat, TrendingUp, Target } from 'lucide-react';
import RecipeIcon from '@/components/common/RecipeIcon';

interface CalendarStatsProps {
  mealPlans: MealPlan[];
  recipes: Recipe[];
  compositions: MealCompositionWithRecipes[];
  isLoading: boolean;
  totalDays: number;
}

const CalendarStats: React.FC<CalendarStatsProps> = ({
  mealPlans,
  recipes,
  compositions,
  isLoading,
  totalDays
}) => {
  // Calculate statistics - count unique meal occasions (date + meal_type combinations)
  const uniqueMealOccasions = new Set(
    mealPlans
      .filter(plan => plan.recipeId || plan.mealCompositionId)
      .map(plan => `${plan.date}-${plan.mealType}`)
  ).size;

  // Calculate dinner-specific metrics (primary use case)
  const dinnerOccasions = new Set(
    mealPlans
      .filter(plan => (plan.recipeId || plan.mealCompositionId) && plan.mealType === 'dinner')
      .map(plan => plan.date)
  ).size;

  const lunchOccasions = new Set(
    mealPlans
      .filter(plan => (plan.recipeId || plan.mealCompositionId) && plan.mealType === 'lunch')
      .map(plan => plan.date)
  ).size;

  // Planning percentage based on dinners (primary focus)
  const dinnerPlanningPercentage = Math.round((dinnerOccasions / totalDays) * 100);
  const overallPlanningPercentage = Math.round((uniqueMealOccasions / (totalDays * 2)) * 100); // Assuming lunch + dinner possibilities

  // Get most frequently planned recipes
  const recipeFrequency = mealPlans
    .filter(plan => plan.recipeId)
    .reduce((acc, plan) => {
      if (plan.recipeId) {
        acc[plan.recipeId] = (acc[plan.recipeId] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

  // Get most frequently planned compositions
  const compositionFrequency = mealPlans
    .filter(plan => plan.mealCompositionId)
    .reduce((acc, plan) => {
      if (plan.mealCompositionId) {
        acc[plan.mealCompositionId] = (acc[plan.mealCompositionId] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

  const topRecipes = Object.entries(recipeFrequency)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3)
    .map(([recipeId, count]) => {
      const recipe = recipes.find(r => r.id === recipeId);
      return { type: 'recipe' as const, item: recipe, count };
    })
    .filter(item => item.item);

  const topCompositions = Object.entries(compositionFrequency)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3)
    .map(([compositionId, count]) => {
      const composition = compositions.find(c => c.id === compositionId);
      return { type: 'composition' as const, item: composition, count };
    })
    .filter(item => item.item);

  // Combine and sort top items
  const topItems = [...topRecipes, ...topCompositions]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const uniqueRecipesCount = Object.keys(recipeFrequency).length;
  const uniqueCompositionsCount = Object.keys(compositionFrequency).length;
  const totalUniqueItems = uniqueRecipesCount + uniqueCompositionsCount;

  // View context and calculations
  const isWeeklyView = totalDays === 7;
  const weeklyAverage = isWeeklyView ? 
    uniqueMealOccasions : // For weekly view, show actual planned meal occasions
    Math.round((uniqueMealOccasions / totalDays) * 7); // For monthly view, calculate weekly average

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Planned Dinners */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-blue-600" />
              Planned Dinners
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dinnerOccasions}</div>
            <p className="text-xs text-muted-foreground">
              out of {totalDays} possible dinners
            </p>
          </CardContent>
        </Card>

        {/* Dinner Planning Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-green-600" />
              Dinner Planning Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dinnerPlanningPercentage}%</div>
            <p className="text-xs text-muted-foreground">of dinners planned</p>
          </CardContent>
        </Card>

        {/* Total Unique Items */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ChefHat className="h-4 w-4 text-orange-600" />
              Unique Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUniqueItems}</div>
            <p className="text-xs text-muted-foreground">
              {uniqueRecipesCount} recipes, {uniqueCompositionsCount} compositions
            </p>
          </CardContent>
        </Card>

        {/* Weekly Average or Total Meals */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-600" />
              {isWeeklyView ? 'Total Meals' : 'Weekly Average'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{weeklyAverage}</div>
            <p className="text-xs text-muted-foreground">
              {isWeeklyView ? 'meals this week' : 'meals per week'}
            </p>
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default CalendarStats;
